import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { FaxItem } from '../../services/faxItems';
import {
  listRouteEpisodes,
  listRouteTasks,
  routeFaxItem,
  type RouteEpisodeOption,
  type RouteFaxResponse,
  type RouteTaskList,
} from '../../services/faxRouting';
import {
  afterSuggestion,
  afterTypeChange,
  afterTyping,
  EMPTY_TASK_NAME,
  type TaskNameState,
} from './routeTaskName';
import {
  getBusinessErrorMessage,
  getErrorConflictCode,
} from '../../utils/apiErrorDetail';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';

const NEW_TASK = '__new__';

interface FileToChartDialogProps {
  /** The fax to file. It must already be saved with a patient. */
  item: FaxItem | null;
  onClose: () => void;
  onFiled: (result: RouteFaxResponse) => void;
  onError: (message: string) => void;
}

function episodeLabel(episode: RouteEpisodeOption): string {
  const range = `${formatDateToMMDDYYYY(episode.start_date)} to ${formatDateToMMDDYYYY(episode.end_date)}`;
  return episode.is_current ? `${range} (current)` : range;
}

/**
 * Put a fax on a task of one of the patient's episodes.
 *
 * Starts on the current episode and on the task Roger suggests for the fax's
 * type, so the common case is one click. Any other task, or a new one of any
 * misc type, can be chosen instead.
 */
export default function FileToChartDialog({
  item,
  onClose,
  onFiled,
  onError,
}: FileToChartDialogProps) {
  const [episodes, setEpisodes] = useState<RouteEpisodeOption[]>([]);
  const [episodeId, setEpisodeId] = useState<string>('');
  const [taskList, setTaskList] = useState<RouteTaskList | null>(null);
  const [destination, setDestination] = useState<string>(NEW_TASK);
  const [newType, setNewType] = useState<string>('');
  const [taskName, setTaskName] = useState<TaskNameState>(EMPTY_TASK_NAME);
  const newName = taskName.name;
  const setNewName = (value: string) => setTaskName(afterTyping(value));
  const [loading, setLoading] = useState(false);
  const [filing, setFiling] = useState(false);

  const patientId = item?.patient_id ?? null;

  useEffect(() => {
    setEpisodes([]);
    setEpisodeId('');
    setTaskList(null);
    setTaskName(EMPTY_TASK_NAME);
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    listRouteEpisodes(patientId)
      .then(found => {
        if (cancelled) return;
        setEpisodes(found);
        const start = found.find(e => e.is_current) ?? found[0];
        setEpisodeId(start?.id ?? '');
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load episodes', error);
        onError("Could not load the patient's episodes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, onError]);

  useEffect(() => {
    setTaskList(null);
    if (!episodeId) return;
    let cancelled = false;
    setLoading(true);
    listRouteTasks(episodeId, item?.fax_type_id)
      .then(found => {
        if (cancelled) return;
        setTaskList(found);
        setDestination(found.suggestion.task_id ?? NEW_TASK);
        setNewType(found.suggestion.misc_task_type);
        setTaskName(prev =>
          afterSuggestion(prev, found.suggestion.task_name ?? null)
        );
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load episode tasks', error);
        onError("Could not load the episode's tasks.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [episodeId, item?.fax_type_id, onError]);

  const file = async () => {
    if (!item || !patientId || !episodeId || filing) return;
    setFiling(true);
    try {
      const result = await routeFaxItem(item.kind, item.id, {
        patient_id: patientId,
        episode_id: episodeId,
        fax_type_id: item.fax_type_id ?? null,
        ...(destination === NEW_TASK
          ? {
              new_task: {
                misc_task_type: newType,
                task_name: newName.trim() || null,
              },
            }
          : { task_id: destination }),
      });
      onFiled(result);
    } catch (error) {
      console.error('Failed to file the fax', error);
      if (getErrorConflictCode(error) === 'FAX_ALREADY_FILED') {
        onError('This fax is already filed to another task.');
      } else {
        onError(getBusinessErrorMessage(error) ?? 'Failed to file the fax.');
      }
    } finally {
      setFiling(false);
    }
  };

  const chartLocked = !!taskList?.chart_locked;
  const canFile =
    !loading &&
    !filing &&
    !chartLocked &&
    // Only a task list for the chosen episode makes the destination valid.
    !!taskList &&
    !!episodeId &&
    (destination !== NEW_TASK || !!newType);

  return (
    <Dialog open={item !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>File to chart</DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <p className='text-sm text-muted-foreground'>
            {item?.patient_label ?? 'Patient'}
          </p>

          <div className='space-y-1.5'>
            <Label htmlFor='route-episode'>Episode</Label>
            <Select
              value={episodeId}
              onValueChange={setEpisodeId}
              disabled={filing || episodes.length === 0}
            >
              <SelectTrigger id='route-episode' aria-label='Episode'>
                <SelectValue
                  placeholder={
                    loading && episodes.length === 0 ? 'Loading' : 'No episodes'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {episodes.map(episode => (
                  <SelectItem key={episode.id} value={episode.id}>
                    {episodeLabel(episode)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {chartLocked && (
            <p className='text-sm text-muted-foreground'>
              This episode&apos;s chart is locked, so nothing can be filed to
              it.
            </p>
          )}

          {taskList && !chartLocked && (
            <div className='space-y-1.5'>
              <Label>File to task</Label>
              <RadioGroup
                value={destination}
                onValueChange={setDestination}
                disabled={filing}
                className='max-h-64 gap-2 overflow-y-auto'
              >
                {taskList.tasks.map(task => (
                  <label
                    key={task.id}
                    className='flex items-start gap-3 rounded border p-2 text-sm'
                  >
                    <RadioGroupItem
                      value={task.id}
                      disabled={!task.accepts_documents}
                      aria-label={task.display_name}
                    />
                    <span className='flex flex-col'>
                      <span className='font-medium'>{task.display_name}</span>
                      <span className='text-xs text-muted-foreground'>
                        {formatDateToMMDDYYYY(task.task_date)}
                        {task.document_count === 1
                          ? ', 1 document'
                          : `, ${task.document_count} documents`}
                        {!task.accepts_documents && ', signed'}
                      </span>
                    </span>
                  </label>
                ))}
                <label className='flex items-start gap-3 rounded border p-2 text-sm'>
                  <RadioGroupItem value={NEW_TASK} aria-label='New task' />
                  <span className='font-medium'>New task</span>
                </label>
              </RadioGroup>
              {taskList.suggestion.hint && (
                <p className='text-xs text-muted-foreground'>
                  {taskList.suggestion.hint}
                </p>
              )}
            </div>
          )}

          {taskList && !chartLocked && destination === NEW_TASK && (
            <div className='grid gap-3 sm:grid-cols-2'>
              <div className='space-y-1.5'>
                <Label htmlFor='route-task-type'>Task type</Label>
                <Select
                  value={newType}
                  onValueChange={value => {
                    setNewType(value);
                    setTaskName(prev =>
                      afterTypeChange(
                        prev,
                        value,
                        taskList.suggestion.misc_task_type,
                        taskList.suggestion.task_name ?? null
                      )
                    );
                  }}
                  disabled={filing}
                >
                  <SelectTrigger id='route-task-type' aria-label='Task type'>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {taskList.misc_task_types.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='route-task-name'>Task name (optional)</Label>
                <Input
                  id='route-task-name'
                  maxLength={120}
                  value={newName}
                  disabled={filing}
                  onChange={event => setNewName(event.target.value)}
                />
              </div>
            </div>
          )}

          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={onClose} disabled={filing}>
              Cancel
            </Button>
            <Button onClick={() => void file()} disabled={!canFile}>
              {filing && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              File to chart
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
